import { useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import type { Comic } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comicId = Number(id);
  const comic = useMemo(() => (isNaN(comicId) ? undefined : getComicById(comicId)), [comicId]);

  const category = useMemo(() => {
    if (!comic) return undefined;
    return categories.find((c) => c.slug === comic.category);
  }, [comic]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 非麻瓜漫画`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `${comic.title} - ${comic.author} | ${comic.description.slice(0, 120)}`);
      }
    }
    return () => {
      document.title = '非麻瓜漫画-免费韩漫在线阅读网站_最新韩国漫画下拉式观看';
    };
  }, [comic]);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 font-heading mb-3">漫画未找到</h1>
            <button onClick={() => navigate('/')} className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const statusColor = comic.status === '连载中' ? 'bg-primary-500' : 'bg-secondary-500';

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: comic.title },
            ]}
          />

          {/* Comic Detail */}
          <article className="py-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="shrink-0 w-full md:w-[300px]">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover" />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className={`${statusColor} text-background-50 text-xs px-2.5 py-1 rounded-full whitespace-nowrap`}>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-accent-500 text-sm">
                    <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-600">
                    {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 阅读
                  </span>
                  <span className="text-sm text-foreground-600">共 {comic.chapters.length} 话</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs text-secondary-600 bg-secondary-100 px-2.5 py-1 rounded whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-xs text-primary-500 bg-primary-50 px-2.5 py-1 rounded hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
                </div>

                <p className="text-xs text-foreground-500">
                  最后更新：{comic.updateTime}
                </p>
              </div>
            </div>

            {/* Chapter List */}
            <section className="mt-10">
              <h2 className="text-lg font-bold text-foreground-950 font-heading mb-4 flex items-center gap-2">
                <i className="ri-list-check w-5 h-5 flex items-center justify-center text-primary-500"></i>
                章节列表
                <span className="text-sm font-normal text-foreground-600">（共 {comic.chapters.length} 话）</span>
              </h2>

              <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-background-200/70">
                  {comic.chapters.map((chapter) => (
                    <div key={chapter.id} className="bg-background-50 px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer flex items-center justify-between group">
                      <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors">
                        {chapter.title}
                      </span>
                      <span className="text-xs text-foreground-500">{chapter.updateTime}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Related Comics - same category */}
            <section className="mt-14">
              <h2 className="text-lg font-bold text-foreground-950 font-heading mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {getComicById(comic.id + 1 > 24 ? 1 : comic.id + 1) && (
                  <ComicCardWrapper comic={getComicById(comic.id + 1 > 24 ? 1 : comic.id + 1)!} />
                )}
                {getComicById(comic.id + 2 > 24 ? (comic.id + 2 - 24) : comic.id + 2) && (
                  <ComicCardWrapper comic={getComicById(comic.id + 2 > 24 ? (comic.id + 2 - 24) : comic.id + 2)!} />
                )}
                {getComicById(comic.id + 3 > 24 ? (comic.id + 3 - 24) : comic.id + 3) && (
                  <ComicCardWrapper comic={getComicById(comic.id + 3 > 24 ? (comic.id + 3 - 24) : comic.id + 3)!} />
                )}
                {getComicById(comic.id + 4 > 24 ? (comic.id + 4 - 24) : comic.id + 4) && (
                  <ComicCardWrapper comic={getComicById(comic.id + 4 > 24 ? (comic.id + 4 - 24) : comic.id + 4)!} />
                )}
              </div>
            </section>
          </article>
        </div>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": comic.title,
          "author": { "@type": "Person", "name": comic.author },
          "description": comic.description,
          "datePublished": comic.chapters[0]?.updateTime || '',
          "dateModified": comic.updateTime,
          "inLanguage": "zh-Hans"
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://feimagua.com" },
            ...(category ? [{ "@type": "ListItem", "position": 2, "name": category.name, "item": `https://feimagua.com/category/${category.slug}` }] : []),
            { "@type": "ListItem", "position": category ? 3 : 2, "name": comic.title, "item": `https://feimagua.com/comic/${comic.id}` }
          ]
        })
      }} />
    </>
  );
}

function ComicCardWrapper({ comic }: { comic: Comic }) {
  return <ComicCard comic={comic} />;
}